import { useState, useEffect, useRef } from "react";
import { Play, X, Maximize2 } from "lucide-react";

// Helper function to determine media type
export const getMediaType = (url: string) => {
  if (!url) return "unknown";

  const extension = url.split(".").pop()?.toLowerCase() ?? "";
  if (["jpg", "jpeg", "png", "gif", "webp"].includes(extension)) {
    return "image";
  } else if (["mp4", "webm", "ogg", "mov"].includes(extension)) {
    return "video";
  } else if (url.includes("video.twimg.com")) {
    // Twitter videos usually have this in the URL
    return "video";
  }

  return "unknown";
};

// Modal component for full-size media display
const MediaModal = ({
  url,
  mediaType,
  isOpen,
  onClose,
}: {
  url: string;
  mediaType: string;
  isOpen: boolean;
  onClose: () => void;
}) => {
  const modalRef = useRef(null);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
      document.body.style.overflow = "hidden"; // Prevent scrolling when modal is open
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "auto"; // Re-enable scrolling when modal is closed
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-75 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        ref={modalRef}
        className="relative max-w-full max-h-full"
        onClick={(e) => e.stopPropagation()} // Prevent closing when clicking the content
      >
        <button
          className="absolute top-2 right-2 bg-black bg-opacity-50 text-white rounded-full p-1 hover:bg-opacity-75"
          onClick={onClose}
        >
          <X size={20} />
        </button>

        {mediaType === "image" ? (
          <img
            src={url}
            alt="Media content"
            className="max-w-full max-h-[90vh] object-contain rounded"
          />
        ) : mediaType === "video" ? (
          <video
            src={url}
            controls
            autoPlay
            className="max-w-full max-h-[90vh]"
          >
            Your browser does not support the video tag.
          </video>
        ) : (
          <div className="bg-gray-800 p-8 rounded-lg">
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-400 underline flex items-center text-lg"
            >
              <Play className="w-6 h-6 mr-2" />
              Open media in new tab
            </a>
          </div>
        )}
      </div>
    </div>
  );
};

// Media component to handle different media types
export const TwitterMedia = ({
  url,
  className = "",
}: {
  url: string;
  className?: string;
}) => {
  const [hasError, setHasError] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const mediaType = getMediaType(url);

  if (hasError) {
    return null;
  }

  const openModal = () => setIsModalOpen(true);
  const closeModal = () => setIsModalOpen(false);

  if (mediaType === "image") {
    return (
      <>
        <div className="relative group">
          <div className="w-full aspect-video bg-gray-100 overflow-hidden">
            <img
              src={url}
              alt="Media attachment"
              className={`w-full h-full object-contain ${className}`}
              onError={() => setHasError(true)}
            />
          </div>
          <button
            onClick={openModal}
            className="absolute top-2 right-2 bg-black bg-opacity-50 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
            aria-label="View full size"
          >
            <Maximize2 size={16} />
          </button>
          <div
            className="absolute inset-0 cursor-pointer bg-transparent"
            onClick={openModal}
            aria-label="View full size"
          />
        </div>
        <MediaModal
          url={url}
          mediaType={mediaType}
          isOpen={isModalOpen}
          onClose={closeModal}
        />
      </>
    );
  } else if (mediaType === "video") {
    return (
      <>
        <div className="relative group">
          <div className="w-full aspect-video bg-gray-100 overflow-hidden">
            <video
              src={url}
              className="w-full h-full object-contain"
              onError={() => setHasError(true)}
            >
              Your browser does not support the video tag.
            </video>
            <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-30 group-hover:bg-opacity-20 transition-opacity">
              <Play className="w-12 h-12 text-white opacity-80" />
            </div>
          </div>
          <div
            className="absolute inset-0 cursor-pointer"
            onClick={openModal}
            aria-label="Play video"
          />
        </div>
        <MediaModal
          url={url}
          mediaType={mediaType}
          isOpen={isModalOpen}
          onClose={closeModal}
        />
      </>
    );
  }

  // Fallback for unknown media types
  return (
    <div
      className={`bg-gray-200 w-full aspect-video flex items-center justify-center rounded ${className}`}
    >
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="text-blue-500 underline flex items-center"
      >
        <Play className="w-4 h-4 mr-1" />
        Open media
      </a>
    </div>
  );
};

// Grid component for displaying multiple media items
export const TwitterMediaGrid = ({
  urls = [],
  className = "",
}: {
  className?: string;
  urls: string[];
}) => {
  if (!urls || urls.length === 0) {
    return null;
  }

  // Determine the grid layout based on number of items and types
  let gridLayout = "grid-cols-1";

  if (urls.length === 1) {
    gridLayout = "grid-cols-1";
  } else if (urls.length === 2) {
    gridLayout = "grid-cols-2";
  } else if (urls.length === 3) {
    gridLayout = "grid-cols-2"; // First item takes full width, others split
  } else if (urls.length >= 4) {
    gridLayout = "grid-cols-2";
  }

  // Check if any videos are present
  const hasVideo = urls.some((url) => getMediaType(url) === "video");
  if (hasVideo) {
    gridLayout = "grid-cols-1"; // If video is present, use single column layout
  }

  return (
    <div className={`mt-3 grid ${gridLayout} gap-2 ${className}`}>
      {urls.length === 3 ? (
        // Special case for 3 items
        <>
          <div className="col-span-2 border rounded overflow-hidden">
            <TwitterMedia url={urls[0]} />
          </div>
          <div className="border rounded overflow-hidden">
            <TwitterMedia url={urls[1]} />
          </div>
          <div className="border rounded overflow-hidden">
            <TwitterMedia url={urls[2]} />
          </div>
        </>
      ) : (
        // Default grid layout
        urls.map((url, index) => (
          <div key={index} className="border rounded overflow-hidden">
            <TwitterMedia url={url} />
          </div>
        ))
      )}
    </div>
  );
};

export default TwitterMediaGrid;
